import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import { NumericKeyboard } from '../auth'
import completePaymentIcon from '../../assets/images/completePayment.svg'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface PaymentInputProps {
  paymentAmount: string
  selectedPaymentMethod: 'cash' | 'network' | null
  remainingAmount: number
  isLastPayment: boolean
  currentPaymentCount: number
  isSubmitting: boolean
  onNumberPress: (number: string) => void
  onDeletePress: () => void
  onPaymentMethodClick: () => void
  onPayClick: () => void
  getPaymentMethodName: (method: 'cash' | 'network') => string
}

const PaymentInput: React.FC<PaymentInputProps> = ({
  paymentAmount,
  selectedPaymentMethod,
  remainingAmount,
  isLastPayment,
  currentPaymentCount,
  isSubmitting,
  onNumberPress,
  onDeletePress,
  onPaymentMethodClick,
  onPayClick,
  getPaymentMethodName,
}) => {
  const { t, i18n } = useTranslation()
  const { settings } = useSelector((state: RootState) => state.settings)
  const isRTL = settings.language === 'ar'
  
  // Ensure i18n language matches settings
  useEffect(() => {
    if (i18n.language !== settings.language) {
      i18n.changeLanguage(settings.language)
    }
  }, [settings.language, i18n])

  // Memoize translations to ensure they update when language changes
  const translations = useMemo(() => ({
    selectPaymentMethod: t('payment.select_payment_method'),
    processing: t('payment.processing'),
    pay: t('payment.pay'),
  }), [t, i18n.language])

  return (
    <div className="h-[560px] gap-2 inline-flex flex-col justify-between items-center">
      {/* Payment Method Selection */}
      <div 
        className="w-full h-14 pl-3 pr-4 bg-white rounded-xl inline-flex justify-between items-center gap-2 overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onPaymentMethodClick}
      >
   <div className="flex flex-row justify-start items-start gap-3">
   <div className=" ">
          <img src={completePaymentIcon} alt={t('payment.payment')} className="w-5 h-5" />
          </div>
  <div className="  text-text-primary text-sm font-medium font-['IBM_Plex_Sans_Arabic'] leading-5">
          {selectedPaymentMethod ? getPaymentMethodName(selectedPaymentMethod) : translations.selectPaymentMethod}
        </div>
       
          
        </div>


<div className=" ">

        {isRTL ? (
          <ChevronLeft className="size-5 text-icon-secondary" />
        ) : (
          <ChevronRight className="size-5 text-icon-secondary" />
        )}
       
</div>
     
      </div>

      {/* Amount Display */}
      <div className="self-stretch py-2 inline-flex justify-center items-center gap-1">
        <div className="text-right justify-start text-text-primary text-3xl font-normal font-['Inter'] leading-10">
          {paymentAmount || '0'}
        </div>
      </div>

      {/* Numeric Keyboard */}
      <div className="w-[480px] flex flex-col justify-center items-center gap-4">
        <NumericKeyboard
          onNumberPress={onNumberPress}
          onDeletePress={onDeletePress}
          disabled={isSubmitting || isLastPayment}
          includeDecimal={true}
        />
      </div>

      {/* Pay Button */}
      <button
        onClick={onPayClick}
        disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || isSubmitting || !selectedPaymentMethod}
        className="w-[480px] h-14 px-4 bg-flutter-button rounded-xl shadow-[inset_0px_0px_0px_1px_rgba(0,0,0,0.25)] inline-flex justify-center items-center gap-2 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="text-center justify-center text-text-primary-on-accent text-base font-semibold font-['IBM_Plex_Sans_Arabic'] leading-6">
          {isSubmitting ? translations.processing : translations.pay}
        </div>
      </button>
    </div>
  )
}

export default PaymentInput

